/**
 * Откаты по игрокам.
 *
 * Раньше здесь висел список несданных запросов по наборам. Запрос ушёл
 * вместе с сообщением в личном канале, а с ним и привязка к набору —
 * человек присылает запись когда придётся. Осталось то, что можно
 * утверждать: сколько прислал, когда в последний раз и что именно.
 */

import { Fragment, useCallback, useEffect, useState, type ReactNode } from 'react';
import { tierLabel, toTier } from '@ak/shared';

import { apiGet } from '../lib/api.ts';
import { formatDateTime, formatRelative } from '../lib/format.ts';
import { useResource } from '../lib/useResource.ts';
import { Badge, EmptyState, ErrorState, PageHeader, Panel, Skeleton } from '../components/ui.tsx';

export interface RollbackPlayer {
  userId: string;
  gameNick: string;
  staticId: string;
  tier: number;
  leftAt: string | null;
  mainCount: number;
  rollbackCount: number;
  lastRollbackAt: string | null;
}

interface RollbackLink {
  messageId: string;
  url: string;
  title: string | null;
  postedAt: string;
}

type SortKey = 'nick' | 'rollbacks' | 'main' | 'last';

const COLUMNS: { key: SortKey; label: string; numeric: boolean }[] = [
  { key: 'nick', label: 'Игрок', numeric: false },
  { key: 'rollbacks', label: 'Откатов', numeric: true },
  { key: 'main', label: 'В основе', numeric: true },
  { key: 'last', label: 'Последний откат', numeric: false },
];

/**
 * Порядок сортировки.
 *
 * По умолчанию наверх поднимаются те, кто ничего не присылал: раздел
 * открывают именно ради них, а не ради прилежных.
 */
export function comparePlayers(left: RollbackPlayer, right: RollbackPlayer, key: SortKey, descending: boolean): number {
  const sign = descending ? -1 : 1;

  switch (key) {
    case 'rollbacks':
      return sign * (left.rollbackCount - right.rollbackCount);
    case 'main':
      return sign * (left.mainCount - right.mainCount);
    case 'last':
      // Никогда не присылавшие идут первыми — им и посвящён раздел.
      return sign * ((left.lastRollbackAt ?? '').localeCompare(right.lastRollbackAt ?? ''));
    default:
      return sign * left.gameNick.localeCompare(right.gameNick, 'ru');
  }
}

function TierMark({ tier }: { tier: number }): ReactNode {
  // Через `toTier`, а затем сравнение с нулём: только равенство сужает
  // объединение тиров до назначенного, `> 0` этого не делает.
  const assigned = toTier(tier);

  return (
    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
      {assigned !== 0 ? tierLabel(assigned) : 'без tier'}
    </span>
  );
}

function LinkList({ userId, timezone }: { userId: string; timezone: string }): ReactNode {
  const [links, setLinks] = useState<RollbackLink[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;

    void apiGet<{ links: RollbackLink[] }>(`/api/rollbacks/${userId}`)
      .then((data) => {
        if (alive) setLinks(data.links);
      })
      .catch(() => {
        if (alive) setFailed(true);
      });

    return () => {
      alive = false;
    };
  }, [userId]);

  if (failed) {
    return (
      <div className="px-4 py-3 text-sm" style={{ color: 'var(--text-muted)' }}>
        Не удалось загрузить откаты.
      </div>
    );
  }

  if (links === null) {
    return <Skeleton rows={2} />;
  }

  if (links.length === 0) {
    return (
      <div className="px-4 py-3 text-sm" style={{ color: 'var(--text-muted)' }}>
        Откатов нет.
      </div>
    );
  }

  return (
    <ul className="space-y-2 px-4 py-3">
      {links.map((link) => (
        <li key={`${link.messageId}-${link.url}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
          <span className="tabular-nums" style={{ color: 'var(--text-muted)' }}>
            {formatDateTime(link.postedAt, timezone)}
          </span>
          <a
            href={link.url}
            target="_blank"
            rel="noreferrer noopener"
            className="underline decoration-dotted underline-offset-4"
            style={{ color: 'var(--accent)' }}
          >
            {link.title ?? link.url}
          </a>
        </li>
      ))}
    </ul>
  );
}

export function RollbacksPage({ timezone }: { timezone: string }): ReactNode {
  const { data, loading, error, reload } = useResource<{ players: RollbackPlayer[] }>('/api/rollbacks');
  const [sort, setSort] = useState<{ key: SortKey; descending: boolean }>({ key: 'last', descending: false });
  const [openUserId, setOpenUserId] = useState<string | null>(null);
  const [showLeft, setShowLeft] = useState(false);

  const toggleSort = useCallback((key: SortKey) => {
    setSort((current) => (current.key === key ? { key, descending: !current.descending } : { key, descending: true }));
  }, []);

  if (error !== null) {
    return <ErrorState message={error} onRetry={reload} />;
  }

  const players = (data?.players ?? [])
    .filter((player) => showLeft || player.leftAt === null)
    .sort((left, right) => comparePlayers(left, right, sort.key, sort.descending));

  return (
    <>
      <PageHeader
        title="Откаты"
        description="Что игроки присылали в свои личные каналы"
        actions={
          <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <input
              type="checkbox"
              checked={showLeft}
              onChange={(event) => {
                setShowLeft(event.target.checked);
              }}
            />
            Показать ушедших
          </label>
        }
      />

      <Panel className="overflow-hidden">
        {loading || data === null ? (
          <Skeleton rows={6} />
        ) : players.length === 0 ? (
          <EmptyState title="Список пуст" description="Анкет пока нет." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-sm">
              <thead>
                <tr style={{ background: 'var(--surface-raised)' }}>
                  {COLUMNS.map((column) => (
                    <th
                      key={column.key}
                      className={`px-4 py-3 text-xs font-semibold tracking-wide uppercase ${column.numeric ? 'text-right' : 'text-left'}`}
                      style={{ color: 'var(--text-muted)' }}
                    >
                      <button
                        type="button"
                        className="cursor-pointer uppercase"
                        onClick={() => {
                          toggleSort(column.key);
                        }}
                      >
                        {column.label}
                        {sort.key === column.key ? (sort.descending ? ' ↓' : ' ↑') : ''}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {players.map((player) => {
                  const open = openUserId === player.userId;

                  return (
                    <Fragment key={player.userId}>
                      <tr
                        className="cursor-pointer border-t"
                        style={{ borderColor: 'var(--border-subtle)' }}
                        onClick={() => {
                          setOpenUserId(open ? null : player.userId);
                        }}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-baseline gap-2">
                            <span className="font-medium">{player.gameNick}</span>
                            <TierMark tier={player.tier} />
                            {player.leftAt === null ? null : <Badge tone="warning">ушёл</Badge>}
                          </div>
                          <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {player.staticId}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{player.rollbackCount}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{player.mainCount}</td>
                        <td className="px-4 py-3" style={{ color: 'var(--text-muted)' }}>
                          {player.lastRollbackAt === null ? 'никогда' : formatRelative(player.lastRollbackAt)}
                        </td>
                      </tr>

                      {open && (
                        <tr style={{ background: 'var(--surface-raised)' }}>
                          <td colSpan={COLUMNS.length} className="p-0">
                            {player.rollbackCount === 0 ? (
                              <div className="px-4 py-3 text-sm" style={{ color: 'var(--text-muted)' }}>
                                Откатов нет. В основе был {String(player.mainCount)} раз.
                              </div>
                            ) : (
                              <LinkList userId={player.userId} timezone={timezone} />
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
